import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    findPendingDocument,
    approveDeleteRequest,
    denyDeleteRequest
} from "../../../../api/documentos/endpoints";
import { decodeNavParams } from "../../../../utils/navParams";
import { z } from "zod";
import { useAuth } from "../../../../auth";
import {
    Stack,
    Typography,
    Button,
    Paper,
    Grid,
    Box,
    CircularProgress,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Alert,
    IconButton
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useState } from "react";
import { DocumentVisualization } from "../components/DocumentVisualization";
import { useTranslation } from "react-i18next";

const analyzeSearchSchema = z.object({
    from: z.string().optional(),
    fromParams: z.string().optional(),
});

export const Route = createFileRoute('/__auth/documents/$pendingDocumentId/analyze')({
    component: AnalyzeDocumentComponent,
    validateSearch: (search) => analyzeSearchSchema.parse(search),
    staticData: { title: "routes.analyzeDocument" }
});

function AnalyzeDocumentComponent() {
    const { t } = useTranslation();
    const { pendingDocumentId } = Route.useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const search = Route.useSearch();
    const queryClient = useQueryClient();

    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [denyDialogOpen, setDenyDialogOpen] = useState(false);
    const [denyReason, setDenyReason] = useState("");
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" | "warning" }>({
        open: false,
        message: "",
        severity: "info"
    });

    const { data: pendingDocument, isLoading, error } = useQuery({
        queryKey: ['PendingDocumentsFindPendingDocument', { pendingDocumentId: Number(pendingDocumentId), tenantId: user?.tenant || "" }],
        queryFn: async () => {
            const res = await findPendingDocument(
                user?.tenant || "",
                Number(pendingDocumentId)
            );
            return (res as any)?.data || res;
        },
        enabled: !!user?.tenant && !!pendingDocumentId
    });

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const approveMutation = useMutation({
        mutationFn: async (data: { pendingDocumentId: number; tenantId: string }) => {
            const res = await approveDeleteRequest(data.tenantId, data.pendingDocumentId);
            return (res as any)?.data || res;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['PendingDocumentsListPendingDocument'] });
            setSnackbar({ open: true, message: t("documents.analysis.approvedSuccess"), severity: "success" });
            setTimeout(() => {
                navigate({ to: '/documents/analysis', search: { page: 1, size: 10, bySituation: 2, sort: 'id:desc' } });
            }, 1000);
        },
        onError: () => {
            setSnackbar({ open: true, message: t("documents.analysis.approveError"), severity: "error" });
        }
    });

    const denyMutation = useMutation({
        mutationFn: async (data: { pendingDocumentId: number; tenantId: string; corpo: any }) => {
            const res = await denyDeleteRequest(
                data.tenantId,
                data.pendingDocumentId,
                data.corpo
            );
            return (res as any)?.data || res;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['PendingDocumentsListPendingDocument'] });
            setSnackbar({ open: true, message: t("documents.analysis.rejectedSuccess"), severity: "success" });
            setTimeout(() => {
                navigate({ to: '/documents/analysis', search: { page: 1, size: 10, bySituation: 2, sort: 'id:desc' } });
            }, 1000);
        },
        onError: () => {
            setSnackbar({ open: true, message: t("documents.analysis.rejectError"), severity: "error" });
        }
    });

    if (isLoading) {
        return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    }

    if (error || !pendingDocument) {
        return <Typography color="error">Erro ao carregar documento ou documento não encontrado.</Typography>;
    }

    const { document, requester, pendingType, createdAt, situation } = pendingDocument;

    const handleApprove = () => {
        approveMutation.mutate({
            pendingDocumentId: Number(pendingDocumentId),
            tenantId: user?.tenant || ""
        });
        setApproveDialogOpen(false);
    };

    const handleDeny = () => {
        denyMutation.mutate({
            pendingDocumentId: Number(pendingDocumentId),
            tenantId: user?.tenant || "",
            corpo: { denyReason }
        });
        setDenyDialogOpen(false);
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString("pt-BR") + " " + new Date(dateString).toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' });
    };

    const handleBack = () => {
        if (search.from === 'analysis' && search.fromParams) {
            const params = decodeNavParams(search.fromParams);
            navigate({
                to: '/documents/analysis',
                search: params as any
            });
        } else {
            navigate({ to: '/documents/analysis', search: { page: 1, size: 10, bySituation: 2, sort: 'id:desc' } });
        }
    };

    return (
        <Stack spacing={3} p={3}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <IconButton onClick={handleBack} color="primary">
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4">Analisar Documento</Typography>
            </Stack>

            <Paper sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" color="textSecondary">Documento</Typography>
                        <Typography variant="body1">{document?.name} (ID: {document?.id})</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" color="textSecondary">Solicitante</Typography>
                        <Typography variant="body1">{requester}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" color="textSecondary">Tipo de Pendência</Typography>
                        <Typography variant="body1">{pendingType?.name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" color="textSecondary">Data da Solicitação</Typography>
                        <Typography variant="body1">{formatDate(createdAt)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" color="textSecondary">Situação Atual</Typography>
                        <Typography variant="body1">{situation?.name}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Visualização do Documento</Typography>
                <DocumentVisualization
                    documentId={document?.id}
                    tenantId={user?.tenant || ""}
                    enabled={!!document?.id}
                />
            </Paper>

            <Divider />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setDenyDialogOpen(true)}
                    disabled={denyMutation.isPending || approveMutation.isPending}
                >
                    Reprovar Solicitação
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setApproveDialogOpen(true)}
                    disabled={denyMutation.isPending || approveMutation.isPending}
                >
                    {approveMutation.isPending ? t("common.loading") : t("documents.approveRequest")}
                </Button>
            </Stack>

            <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Confirmar Aprovação</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja aprovar esta solicitação?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApproveDialogOpen(false)} color="inherit">Cancelar</Button>
                    <Button onClick={handleApprove} variant="contained" color="primary">
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={denyDialogOpen} onClose={() => setDenyDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reprovar Solicitação</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Por favor, informe o motivo da reprovação:
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="reason"
                        label={t("documents.reason")}
                        type="text"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={4}
                        value={denyReason}
                        onChange={(e) => setDenyReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDenyDialogOpen(false)} color="inherit">Cancelar</Button>
                    <Button onClick={handleDeny} variant="contained" color="error" disabled={!denyReason.trim()}>
                        Confirmar Reprovação
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Stack>
    );
}
